import { QuestionCircleOutlined } from '@ant-design/icons';
import { Button, Col, Modal, Row, Space, Typography } from 'antd';
import 'chart.js/auto';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';

import { MindmatchAttributes } from '../../../types';
import PolarAreaChartWithLabel from './PolarAreaChartWithLabel';
import { findTwoLargestValuesIndex } from './Utils/findTwoLargestValuesIndex';

interface AttributeProps {
  attributesData?: MindmatchAttributes[];
  hasAttributes: boolean;
}

const TestProfileMap: React.FC<AttributeProps> = ({ attributesData, hasAttributes = false }) => {
  const { t } = useTranslation();

  const order = [
    // Atributos mentais
    t('Capacidade analítica'),
    t('Pensamento conceitual'),
    t('Reflexão'),
    t('Pensamento criativo'),
    t('Planejamento e organização'),
    // Atributos sociais
    t('Comunicação'),
    t('Consideração pelos outros'),
    t('Influência'),
    t('Sociabilidade'),
    t('Facilitação'),
    t('Flexibilidade'),
    // Atributos motivacionais
    t('Estabilidade emocional'),
    t('Ambição'),
    t('Iniciativa'),
    t('Assertividade'),
    t('Tomada de riscos'),
  ];

  const labels: string[] = [];
  const data: number[] = [];

  if (attributesData) {
    order.forEach((value: string) => {
      const attributeObject = attributesData.find(
        (attribute_dict) => attribute_dict['name'] === value
      );
      if (attributeObject) {
        labels.push(value);
        data.push(attributeObject['value']);
      }
    });
  }

  let backgroundColor;
  if (hasAttributes) {
    backgroundColor = [
      // Azul - Mental
      '#018B97',
      '#018B97',
      '#018B97',
      '#018B97',
      '#018B97',
      // Verde - Social
      '#00CC85',
      '#00CC85',
      '#00CC85',
      '#00CC85',
      '#00CC85',
      '#00CC85',
      // Vermelho - Motivacional
      '#A44A3F',
      '#A44A3F',
      '#A44A3F',
      '#A44A3F',
      '#A44A3F',
    ];
  } else {
    const darkBlue = '#002529';
    backgroundColor = [
      '#018B97',
      '#018B97',
      '#018B97',
      '#018B97',
      '#018B97',
      '#018B97',
      '#018B97',
      '#018B97',
      '#018B97',
      '#018B97',
      '#018B97',
      '#018B97',
      '#018B97',
      '#018B97',
      '#018B97',
      '#018B97',
    ];

    const twoLargestValuesIndex = findTwoLargestValuesIndex(data);
    backgroundColor[twoLargestValuesIndex[0]] = darkBlue;
    backgroundColor[twoLargestValuesIndex[1]] = darkBlue;
  }

  const [isInfoModalOpen, setIsInfoModalOpen] = useState<boolean>(false);

  return (
    <Col>
      {isInfoModalOpen && (
        <Modal
          title={<Typography.Title level={3}>{t('Atributos')}</Typography.Title>}
          open={isInfoModalOpen}
          width={900}
          cancelButtonProps={{
            hidden: true,
          }}
          onOk={() => setIsInfoModalOpen(false)}
        >
          <Helmet>
            <title>
              {t('Atributos')} | {t('Recrutamento e Seleção')}
            </title>
          </Helmet>
          <Typography.Paragraph>
            <p>
              <strong>
                <Trans>Capacidade analítica:</Trans>{' '}
              </strong>
              <Trans>
                Capacidade para analisar informações, números, dados, etc; processar estas
                informações de maneira lógica; chegar a conclusões fundamentadas.
              </Trans>
            </p>
            <p>
              <strong>
                <Trans>Pensamento conceitual:</Trans>{' '}
              </strong>
              <Trans>
                Capacidade de formular conceitos, determinar problemas e lidar com abstrações.
              </Trans>
            </p>
            <p>
              <strong>
                <Trans>Reflexão:</Trans>{' '}
              </strong>
              <Trans>
                Capacidade de reflexão, autoanálise e observação dos seus estados e pontos fortes e
                fracos.
              </Trans>
            </p>
            <p>
              <strong>
                <Trans>Pensamento criativo:</Trans>{' '}
              </strong>
              <Trans>Capacidade de criar coisas novas e inovar; fugir do lugar comum.</Trans>
            </p>
            <p>
              <strong>
                <Trans>Planejamento e organização:</Trans>{' '}
              </strong>
              <Trans>Capacidade de manter as coisas em ordem; controlar e colocar estrutura.</Trans>
            </p>
            <p>
              <strong>
                <Trans>Comunicação:</Trans>{' '}
              </strong>
              <Trans>
                Capacidade de se comunicar verbalmente de forma clara; transmitir ideias através de
                palavras para indivíduos ou audiências.
              </Trans>
            </p>
            <p>
              <strong>
                <Trans>Consideração pelos outros:</Trans>{' '}
              </strong>
              <Trans>
                Capacidade de empatizar e de levar a opinião e o sentimento das outras pessoas em
                consideração nas interações e nas decisões.
              </Trans>
            </p>
            <p>
              <strong>
                <Trans>Influência:</Trans>{' '}
              </strong>
              <Trans>
                Capacidade de persuadir outras pessoas a se comportar de maneira diferente do que
                teriam sem intervenção.
              </Trans>
            </p>
            <p>
              <strong>
                <Trans>Sociabilidade:</Trans>{' '}
              </strong>
              <Trans>
                Capacidade de interagir e criar relacionamento com pessoas diferentes; facilidade em
                ambientes sociais.
              </Trans>
            </p>
            <p>
              <strong>
                <Trans>Facilitação:</Trans>{' '}
              </strong>
              <Trans>
                Capacidade de entender situações sociais, colaborar e solucionar conflitos de
                maneira harmoniosa.
              </Trans>
            </p>
            <p>
              <strong>
                <Trans>Flexibilidade:</Trans>{' '}
              </strong>
              <Trans>Capacidade de adaptar-se a diferentes ambientes de maneira sutil.</Trans>
            </p>
            <p>
              <strong>
                <Trans>Estabilidade emocional:</Trans>{' '}
              </strong>
              <Trans>
                Capacidade de manter a calma em situações de pressão e de controlar altos e baixos
                emocionais.
              </Trans>
            </p>
            <p>
              <strong>
                <Trans>Ambição:</Trans>{' '}
              </strong>
              <Trans>
                Capacidade de auto motivar-se na busca de altos padrões e de crescimento.
              </Trans>
            </p>
            <p>
              <strong>
                <Trans>Iniciativa:</Trans>{' '}
              </strong>
              <Trans>
                Capacidade de iniciar ações sem a necessidade de intervenção de terceiros; tomar a
                frente.
              </Trans>
            </p>
            <p>
              <strong>
                <Trans>Assertividade:</Trans>{' '}
              </strong>
              <Trans>
                Capacidade de colocar as suas ideias de forma direta; não se deixar intimidar pelo
                ambiente.
              </Trans>
            </p>
            <p>
              <strong>
                <Trans>Tomada de riscos:</Trans>{' '}
              </strong>
              <Trans>Capacidade de agir mesmo com altos graus de incerteza, sem se abalar.</Trans>
            </p>
          </Typography.Paragraph>
        </Modal>
      )}
      <Row>
        <Space size="small" align="center">
          <Typography.Title level={5}>
            <Trans>Mapeamento de Perfil</Trans>
          </Typography.Title>
          <Button
            type="link"
            htmlType="submit"
            icon={<QuestionCircleOutlined />}
            style={{ paddingBottom: '10px' }}
            onClick={() => setIsInfoModalOpen(true)}
          />
        </Space>
      </Row>
      <PolarAreaChartWithLabel
        labels={labels}
        data={data}
        backgroundColor={backgroundColor}
        labelFontSize={10}
      />
    </Col>
  );
};

export default TestProfileMap;
